import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { createCompanyProfile, getCountries, getSectors } from '@/lib/api';
import {
  Building2,
  ChevronRight,
  ChevronLeft,
  Check,
  Briefcase,
  DollarSign,
  Phone,
  Sparkles,
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_creditai-africa/artifacts/q9fyvy5m_ChatGPT%20Image%20Feb%201%2C%202026%2C%2012_34_17%20PM.png";

const STEPS = [
  { id: 1, title: 'Company Info', icon: Building2, description: 'Basic company details' },
  { id: 2, title: 'Business Details', icon: Briefcase, description: 'Industry and operations' },
  { id: 3, title: 'Financials', icon: DollarSign, description: 'Financial information' },
  { id: 4, title: 'Contact', icon: Phone, description: 'Contact details' },
];

export default function CompanyOnboarding({ onComplete }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [sectors, setSectors] = useState([]);
  
  const [formData, setFormData] = useState({
    company_name: '',
    registration_number: '',
    year_established: '',
    country: '',
    sector: '',
    sub_sector: '',
    description: '',
    employee_count: '',
    annual_revenue: '',
    website: '',
    contact_phone: '',
    contact_address: '',
  });

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [countriesData, sectorsData] = await Promise.all([
          getCountries(),
          getSectors(),
        ]);
        setCountries(countriesData);
        setSectors(sectorsData);
      } catch (error) {
        console.error('Error loading reference data:', error);
      }
    };
    loadReferenceData();
  }, []);

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = (step) => {
    switch (step) {
      case 1:
        return formData.company_name.trim() !== '';
      case 2:
        return formData.country !== '' && formData.sector !== '';
      case 3:
        return true;
      case 4:
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!validateStep(currentStep)) {
      toast.error('Please fill in the required fields');
      return;
    }
    if (currentStep < STEPS.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const data = {
        company_name: formData.company_name,
        registration_number: formData.registration_number || null,
        year_established: formData.year_established ? parseInt(formData.year_established) : null,
        country: formData.country,
        sector: formData.sector,
        sub_sector: formData.sub_sector || null,
        description: formData.description || null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        annual_revenue: formData.annual_revenue ? parseFloat(formData.annual_revenue) : null,
        website: formData.website || null,
        contact_phone: formData.contact_phone || null,
        contact_address: formData.contact_address || null,
      };

      await createCompanyProfile(data);
      toast.success('Profile created successfully! Welcome to Apaka.');
      
      if (onComplete) {
        onComplete();
      } else {
        navigate('/company');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create profile');
    } finally {
      setLoading(false);
    }
  };

  const progressPercentage = ((currentStep) / STEPS.length) * 100;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Apaka" className="h-12" />
          <button
            onClick={() => navigate('/company')}
            className="text-sm text-slate-500 hover:text-slate-700"
          >
            Skip for now
          </button>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Welcome Message */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-heading font-bold text-slate-900 mb-2">
            Welcome, {user?.full_name?.split(' ')[0]}! 👋
          </h1>
          <p className="text-slate-600">Let's set up your company profile to get started</p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-slate-700">Profile Setup Progress</span>
            <span className="text-sm text-slate-500">{Math.round(progressPercentage)}% Complete</span>
          </div>
          <Progress value={progressPercentage} className="h-2" />
        </div>

        {/* Step Indicators */}
        <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
          {STEPS.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <button
                onClick={() => currentStep > step.id && setCurrentStep(step.id)}
                disabled={currentStep < step.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  currentStep === step.id
                    ? 'bg-blue-600 text-white'
                    : currentStep > step.id
                    ? 'bg-emerald-100 text-emerald-700 cursor-pointer hover:bg-emerald-200'
                    : 'bg-slate-100 text-slate-400'
                }`}
              >
                {currentStep > step.id ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <step.icon className="h-4 w-4" />
                )}
                <span className="text-sm font-medium whitespace-nowrap">{step.title}</span>
              </button>
              {index < STEPS.length - 1 && (
                <div className={`hidden sm:block w-8 h-0.5 mx-2 ${
                  currentStep > step.id ? 'bg-emerald-300' : 'bg-slate-200'
                }`} />
              )}
            </div>
          ))}
        </div>

        {/* Form Card */}
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-100">
            <div className="flex items-center gap-3">
              {(() => {
                const StepIcon = STEPS[currentStep - 1].icon;
                return (
                  <div className="p-2 bg-blue-50 rounded-lg">
                    <StepIcon className="h-5 w-5 text-blue-600" />
                  </div>
                );
              })()}
              <div>
                <CardTitle className="text-lg">{STEPS[currentStep - 1].title}</CardTitle>
                <CardDescription>{STEPS[currentStep - 1].description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <Label>Company Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => updateForm('company_name', e.target.value)}
                    placeholder="e.g., Acme Technologies Ltd"
                    data-testid="onboard-company-name"
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Registration Number</Label>
                    <Input
                      value={formData.registration_number}
                      onChange={(e) => updateForm('registration_number', e.target.value)}
                      placeholder="e.g., RC123456"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Year Established</Label>
                    <Input
                      type="number"
                      value={formData.year_established}
                      onChange={(e) => updateForm('year_established', e.target.value)}
                      placeholder="e.g., 2015"
                    />
                  </div>
                </div>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-blue-800 font-medium">Why we need this</p>
                      <p className="text-xs text-blue-600 mt-1">
                        Your company details help funders understand your business and enable AI-powered matching with the right investors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Business Details */}
            {currentStep === 2 && (
              <div className="space-y-6 animate-fade-in">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Country <span className="text-red-500">*</span></Label>
                    <Select value={formData.country} onValueChange={(v) => updateForm('country', v)}>
                      <SelectTrigger data-testid="onboard-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map((c) => (
                          <SelectItem key={c.code} value={c.name}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Industry Sector <span className="text-red-500">*</span></Label>
                    <Select value={formData.sector} onValueChange={(v) => updateForm('sector', v)}>
                      <SelectTrigger data-testid="onboard-sector">
                        <SelectValue placeholder="Select sector" />
                      </SelectTrigger>
                      <SelectContent>
                        {sectors.map((s) => (
                          <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Number of Employees</Label>
                  <Select value={formData.employee_count} onValueChange={(v) => updateForm('employee_count', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">1-10</SelectItem>
                      <SelectItem value="50">11-50</SelectItem>
                      <SelectItem value="100">51-100</SelectItem>
                      <SelectItem value="250">101-250</SelectItem>
                      <SelectItem value="500">251-500</SelectItem>
                      <SelectItem value="1000">500+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Company Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => updateForm('description', e.target.value)}
                    placeholder="Tell us about your business, what you do, and your growth story..."
                    className="min-h-[100px]"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Financials */}
            {currentStep === 3 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <Label>Annual Revenue (USD)</Label>
                  <Select value={formData.annual_revenue} onValueChange={(v) => updateForm('annual_revenue', v)}>
                    <SelectTrigger data-testid="onboard-revenue">
                      <SelectValue placeholder="Select revenue range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="100000">Under $100,000</SelectItem>
                      <SelectItem value="500000">$100,000 - $500,000</SelectItem>
                      <SelectItem value="1000000">$500,000 - $1 million</SelectItem>
                      <SelectItem value="5000000">$1 million - $5 million</SelectItem>
                      <SelectItem value="10000000">$5 million - $10 million</SelectItem>
                      <SelectItem value="50000000">$10 million - $50 million</SelectItem>
                      <SelectItem value="100000000">$50 million+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <DollarSign className="h-5 w-5 text-emerald-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-emerald-800 font-medium">Financial Privacy</p>
                      <p className="text-xs text-emerald-600 mt-1">
                        Your financial data is kept confidential and only shared with funders you choose to engage with.
                      </p>
                    </div>
                  </div>
                </div>
                <p className="text-center text-slate-500 text-sm">
                  You can add more detailed financials later when creating funding applications.
                </p>
              </div>
            )}

            {/* Step 4: Contact */}
            {currentStep === 4 && (
              <div className="space-y-6 animate-fade-in">
                <div className="space-y-2">
                  <Label>Website</Label>
                  <Input
                    value={formData.website}
                    onChange={(e) => updateForm('website', e.target.value)}
                    placeholder="https://yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone Number</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => updateForm('contact_phone', e.target.value)}
                    placeholder="+234 800 000 0000"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Business Address</Label>
                  <Textarea
                    value={formData.contact_address}
                    onChange={(e) => updateForm('contact_address', e.target.value)}
                    placeholder="Enter your business address..."
                  />
                </div>
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <Check className="h-5 w-5 text-amber-600 mt-0.5" />
                    <div>
                      <p className="text-sm text-amber-800 font-medium">Almost done!</p>
                      <p className="text-xs text-amber-600 mt-1">
                        After completing setup, you can create your first funding application and get matched with investors.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
              <Button
                variant="ghost"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="text-slate-600"
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
              
              {currentStep < STEPS.length ? (
                <Button
                  onClick={nextStep}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="onboard-next"
                >
                  Continue
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="onboard-complete"
                >
                  {loading ? 'Creating Profile...' : 'Complete Setup'}
                  <Check className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
